import { useSearchParams } from 'react-router'
import { SearchParams } from '../../types'
import { prune } from '../../utils'
import { DEFAULT_ITEMS_PER_PAGE, DEFAULT_PAGE, SearchTableQueryState } from './TableTypes'
import { useCallback, useMemo, useState } from 'react'
import { useResolver } from '../../hooks'

export const toTableParams = (searchParams: URLSearchParams): SearchParams => {
    return {
        cursor: searchParams.get('cursor') ?? undefined,
        cursor_direction: searchParams.get('page') === 'prev' ? 'prev' : 'next',
        page: parseInt(searchParams.get('page') ?? '1'),
        limit: parseInt(searchParams.get('limit') ?? '25'),
        q: searchParams.get('q') ?? undefined,
        sort: searchParams.get('sort') ?? undefined,
        direction: searchParams.get('direction') ?? undefined,
        filter: searchParams.get('filter')
            ? JSON.parse(searchParams.get('filter')!)
            : undefined,
    }
}

export const fromTableParams = (params: Partial<SearchParams>): Record<string, string> => {
    return prune({
        cursor: params.cursor,
        cursor_direction: params.cursor_direction,
        page: params.page,
        limit: (params.limit ?? DEFAULT_ITEMS_PER_PAGE).toString(),
        q: params.q,
        sort: params.sort,
        direction: params.direction,
        filter: params.filter ? JSON.stringify(params.filter) : undefined,
    })
}

export const useTableSearchParams = (initialParams: Partial<SearchParams> = {}) => {
    const [searchParams, setSearchParams] = useSearchParams({
        page: DEFAULT_PAGE.toString(),
        ...fromTableParams(initialParams),
    })

    const setParams = useCallback<(params: SearchParams | ((prev: SearchParams) => SearchParams)) => void>(next => {
        typeof next === 'function'
            ? setSearchParams(prev => fromTableParams(next(toTableParams(prev))))
            : setSearchParams(fromTableParams(next))
    }, [setSearchParams])

    const str = searchParams.toString()

    return useMemo(() => [
        toTableParams(new URLSearchParams(str)),
        setParams,
    ] as const, [str, setParams])
}

/**
 * local state
 */
export function useSearchTableState<T>(loader: (params: SearchParams) => Promise<T | null>, initialParams?: Partial<SearchParams>) {

    const [params, setParams] = useState<SearchParams>({
        limit: 25,
        q: '',
        ...initialParams ?? {},
    })

    const [results,, reload] = useResolver(useCallback(async () => await loader(params), [loader, params]))

    return {
        params,
        reload,
        results,
        setParams,
    }
}

export function useSearchTableQueryState<T>(loader: (params: SearchParams) => Promise<T | null>, initialParams?: Partial<SearchParams>): SearchTableQueryState<T> {

    const [params, setParams] = useTableSearchParams(initialParams)

    const [results,, reload] = useResolver(useCallback(async () => await loader(params), [loader, params]))

    return {
        params,
        reload,
        results,
        setParams,
    }
}
